import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, map, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

export interface Tutor {
  id: string;
  client_id: number;
  endpoint_id: number;
  name: string;
  /** Legacy union list (== tutor-purpose KBs only). Kept for callers
   *  that don't yet know about the split. */
  data_source_ids?: string[];
  /** Knowledge bases assigned to the Tutor agent (purpose='tutor'). */
  tutor_data_source_ids?: string[];
  /** Knowledge bases assigned to the Dozent agent (purpose='dozent'). */
  dozent_data_source_ids?: string[];
  description: string;
  created_at: Date;
  updated_at: Date;
  widget_setting_id?: string;
  ai_hint: boolean;
  template_id?: string;
  visual_name?: string;
  /** alpha_dozent bot attached via bot_dozent_link, or null. Drives
   *  the lecturer badge on the tutor card. Populated by the backend
   *  list endpoint with one JOIN, so no per-card lookups are needed. */
  attached_dozent_id?: string | null;
  attached_dozent_name?: string | null;
  /** Who created this agent. Historical fact, independent of the current
   *  owner — the backend never reassigns it. Both stay null for agents made
   *  before the column existed and for demo-provisioned copies, which the UI
   *  renders as an unknown creator rather than guessing. */
  created_by_user_id?: string | null;
  created_by_name?: string | null;
  /** In wie vielen LMS-Kursen dieser Agent für Lernende sichtbar ist.
   *  Gezählt werden Kurse, nicht Platzierungen — mehrere Blöcke im selben
   *  Kurs sind ein Kurs. Kommt aus derselben Tabelle wie die Seite
   *  „Platzierungen“ (`lms_placements`), aggregiert im Listen-Endpunkt,
   *  damit keine Kachel selbst nachladen muss. */
  live_course_count?: number;
}

export interface TutorTemplate {
  description: string;
  endpoint_id: number;
  name: string;
  id: string;
}

/** Bot template `name`s that are no longer offered for new tutor creation.
 *  Existing tutors using these templates keep working — only the create
 *  dropdowns hide them. Add a name here to retire a template without
 *  having to delete its DB row or migrate existing bots. */
export const DEPRECATED_TUTOR_TEMPLATES = new Set<string>([
  'qa_basic',
  'qa_advanced',
  // tutor_basic is superseded by the alpha_* family (alpha_tutor /
  // alpha_assist / alpha_flexible). Hidden from new-tutor creation
  // but kept in the DB so existing tutor_basic bots keep working —
  // re-enable by removing this line if needed.
  'tutor_basic',
]);

/** Bot template `name`s that support being linked to an `alpha_dozent`
 *  (lecturer subgraph routing). Mirrors al-tutor's
 *  `dozent_link_ops.TUTOR_TEMPLATE_NAMES` — keep in sync. Used by the
 *  placements UI / dozent-link picker to disable the toggle when an
 *  incompatible template (alpha_assist, alpha_support, legacy
 *  qa_basic, etc.) is selected, so users can't trigger the backend's
 *  `tutor_template_not_dozent_compatible` 400 by clicking. */
export const DOZENT_COMPATIBLE_TUTOR_TEMPLATES = new Set<string>([
  'alpha_tutor',
  'alpha_flexible',
]);

export interface TutorSettingItem {
  setting_id: number | string;
  /** Bot the setting row was resolved for — guards against saving a stale
   *  item onto a different bot after switching tutors. */
  bot_id?: string;
  name: string;
  variable_name?: string;
  field_type:
    | 'multi-select'
    | 'text'
    | 'boolean'
    | 'dropdown'
    | 'int'
    | 'slider'
    | 'longtext'
    | 'code';
  is_required: boolean;
  default_value: string | number | boolean;
  value?: any;
  options: string[];
  template_id: string;
  // Wizard meta (nullable for legacy settings)
  group_key?: string | null;
  group_order?: number | null;
  display_order?: number | null;
  visibility_rule?: { when: string; equals?: any; not_equals?: any } | null;
  validation?: { min?: number; max?: number; step?: number; regex?: string } | null;
}

/** One provider row from the central model catalog (al-tutor
 *  `GET /model-catalog`). Concrete small/large model ids come from the
 *  backend `MODEL_CATALOG`; `credit_multiplier`/`credit_display` are the
 *  admin-tunable credit-tier hint (from `credit_model_setups`). Read-only —
 *  selecting a provider still writes the `ai_provider` template setting. */
export interface ModelCatalogProvider {
  provider: string;
  /** Picker tab: 'top' | 'flagship' | 'value'. Drives the uniform per-segment
   *  credit multiplier + the badge colour. */
  segment: string;
  backend: string;
  /** The answer model — the ONLY model shown to the portal user. The small
   *  router model is internal and never sent. */
  model: string;
  /** Display name from the catalog's [labels] (e.g. 'Claude Opus 5.5');
   *  older backends send none — fall back to the id. */
  model_label?: string;
  credit_multiplier: number;
  /** Internal/admin label; the user-facing tab name comes from i18n. */
  credit_display?: string;
  /** Whether the agent loop can run on this model ([agent.*] in the model
   *  catalog). Older backends send none. */
  agent?: ModelCatalogAgent;
  /** Speed variant: 'fast' (OpenAI service tier fast — ~2x faster, 2x
   *  credits), 'ultrafast' (OpenAI ultrafast tier or Groq hardware). */
  speed?: 'fast' | 'ultrafast' | null;
  /** light | standard | strong | frontier — orders a vendor's models. */
  model_class?: string | null;
}

/** One segment's uniform pricing, for the credit table below the list. */
/** certified: the agent loop runs on this model · hint: runs on it, weaker
 *  on the `weak_on` cases · answer_only: the loop stays on our model, this
 *  model only writes the answer. */
export interface ModelCatalogAgent {
  status: 'certified' | 'hint' | 'answer_only';
  /** complex | off_topic | en */
  weak_on: string[];
  /** false = the answer arrives in one piece instead of streaming. */
  stream: boolean;
}

export interface ModelCatalogTier {
  segment: string;
  /** Lowest factor offered in the segment (older field, = multiplier_min). */
  multiplier: number;
  /** Span of the factors of the providers offered in the segment — the
   *  factor belongs to the provider (al-user-management migration 079). */
  multiplier_min?: number;
  multiplier_max?: number;
  display_name?: string;
}

export interface ModelCatalog {
  providers: ModelCatalogProvider[];
  /** Tab order, most capable → cheapest. */
  segments: string[];
  tiers: ModelCatalogTier[];
  /** ISO date the concrete models were last upgraded. */
  last_updated?: string;
  /** Where the agent loop runs: legacy (always our model) | opt_in (per
   *  agent, `agent_loop_on_selected_model`) | on (every agent). */
  agent_loop?: 'legacy' | 'opt_in' | 'on';
}

export interface AttachedDozent {
  dozent_bot_id: string;
  name: string;
  description?: string;
}

export interface MoodleDozentCandidateCourse {
  moodle_course_id: number;
  name: string | null;
  /** The course is served by this Twin (directly or via a linked tutor). */
  in_scope: boolean;
}

export interface MoodleDozentCandidate {
  moodle_user_uuid: string;
  moodle_user_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  roles: string[];
  courses: MoodleDozentCandidateCourse[];
  in_scope: boolean;
  selected: boolean;
}

export interface MoodleDozentAccess {
  enabled: boolean;
  audience: 'all_teachers' | 'selected' | string;
  /** Without Moodle PII sync the mirror has no names — only numeric ids. */
  pii_enabled: boolean;
  twin_course_count: number;
  candidates: MoodleDozentCandidate[];
  selected: string[];
  site_admins_note: boolean;
}

export interface DozentBot {
  id: string;
  name: string;
  description?: string;
  template_id: string;
  template_name: string;
  /** Display name of the user who owns this dozent-bot (UserBotAccess
   *  row with access_level='owner'). Falls back to email if the user
   *  has no display name. May be undefined for legacy bots without an
   *  owner row. */
  created_by_name?: string;
}

export interface LinkedTutorSummary {
  id: string;
  name: string;
  template_name: string;
}

/** Candidate tutor/flexible bot a twin can be linked (or requested) to. */
export interface LinkableTutor {
  id: string;
  name: string;
  template_name: string;
  /** Already linked to THIS twin. */
  already_linked: boolean;
  /** Linked to a DIFFERENT twin — approving/attaching would swap it. */
  linked_to_other_twin: boolean;
}

export type LinkRequestStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'cancelled';

/** A rights-limited dozent's request to link their twin to a tutor. */
export interface BotLinkRequest {
  id: string;
  client_id: string;
  requester_user_id?: string | null;
  requester_name?: string | null;
  dozent_bot_id: string;
  dozent_bot_name?: string | null;
  target_tutor_bot_id: string;
  target_tutor_name?: string | null;
  target_template?: string | null;
  status: LinkRequestStatus;
  requester_note?: string | null;
  decision_note?: string | null;
  decided_by_user_id?: string | null;
  created_at?: string | null;
  decided_at?: string | null;
}

export interface CourseKBRow {
  course_id: string;
  /** Tutor-purpose KB. Optional when only a dozent KB is set on this row. */
  kb_id?: string | null;
  /** Optional dozent-purpose KB. Searched only by the lecturer subgraph. */
  kb_dozent_id?: string | null;
  moodle_placement_id?: string | null;
  is_active: boolean;
}

/** Per-bot FAQ entry surfaced for alpha_support / alpha_assist. */
export interface BotFaq {
  id: string;
  bot_id: string;
  client_id: string;
  topic?: string | null;
  tags: string[];
  question: string;
  answer: string;
  is_active: boolean;
  sort_order: number;
}

export interface BotFaqInput {
  topic?: string | null;
  tags?: string[];
  question: string;
  answer: string;
  is_active?: boolean;
  sort_order?: number;
}

@Injectable({
  providedIn: 'root',
})
export class TutorService {
  private baseUrl = `${environment.tutorsBackend}`;

  tutors$ = new BehaviorSubject<Tutor[]>([]);

  tutorTemplates$ = new BehaviorSubject<TutorTemplate[]>([]);
  creatableTutorTemplates$ = new BehaviorSubject<TutorTemplate[]>([]);
  tutorSettings$ = new BehaviorSubject<TutorSettingItem[]>([]);
  /** The agent whose settings `tutorSettings$` holds (null = none). Lets the
   *  agent shell keep them on return to the same agent instead of blanking. */
  tutorSettingsOwner: string | null = null;
  connections$ = new BehaviorSubject<any[]>([]);
  loading$ = new BehaviorSubject(false);

  constructor(
    private http: HttpClient,
    private stateCleanupService: StateCleanupService,
    private translate: TranslateService
  ) {
    this.stateCleanupService.register(() => this.resetState());
  }

  private isTemplateDefaultKey(raw: unknown): raw is string {
    return typeof raw === 'string' && raw.startsWith('templateDefaults.');
  }

  private resolveTemplateDefault(raw: unknown): unknown {
    if (!this.isTemplateDefaultKey(raw)) return raw;
    const translated = this.translate.instant(raw);
    return translated && translated !== raw ? translated : raw;
  }

  private normalizeTemplateDefaultSetting(
    setting: TutorSettingItem
  ): TutorSettingItem {
    return {
      ...setting,
      default_value: this.resolveTemplateDefault(
        setting.default_value
      ) as TutorSettingItem['default_value'],
      value: this.resolveTemplateDefault(setting.value),
    };
  }

  private normalizeTemplateDefaultSettings(
    settings: TutorSettingItem[]
  ): TutorSettingItem[] {
    return settings.map((setting) =>
      this.normalizeTemplateDefaultSetting(setting)
    );
  }

  listTutors() {
    this.loading$.next(true);
    return this.http.get(`${this.baseUrl}/tutor`).pipe(
      tap((data: any) => {
        this.tutors$.next(data.data);
        this.loading$.next(false);
      })
    );
  }

  listTutorTemplates() {
    this.loading$.next(true);
    return this.http.get(`${this.baseUrl}/tutor-templates`).pipe(
      map((data: any) => data.data),
      tap((data: any) => {
        this.tutorTemplates$.next(data);
        this.loading$.next(false);
      })
    );
  }

  listCreatableTutorTemplates() {
    this.loading$.next(true);
    return this.http.get(`${this.baseUrl}/tutor-templates/creatable`).pipe(
      map((data: any) => data.data),
      tap((data: any) => {
        this.creatableTutorTemplates$.next(data);
        this.loading$.next(false);
      })
    );
  }

  /** Templates that admins may pick when creating a new tutor. Excludes
   *  legacy templates that we no longer want to seed new tutors from but
   *  still need to keep around so existing tutors don't break. The Edit
   *  mode keeps using the full `tutorTemplates$` list for label lookup. */
  getCreatableTemplates(templates: TutorTemplate[]): TutorTemplate[] {
    return templates.filter((t) => !DEPRECATED_TUTOR_TEMPLATES.has(t.name));
  }

  listConnections() {
    return this.http.get(`${environment.tutorsBackend}/active-sessions`).pipe(
      map((data: any) => data.data),
      tap((data: any) => {
        this.connections$.next(data.bots);
      })
    );
  }

  getTutor(id: string) {
    this.loading$.next(true);
    return this.http.get(`${this.baseUrl}/tutor/${id}`).pipe(
      map((data: any) => data.data),
      tap((tutor: any) => {
        const existingTutors = this.tutors$.value.find(
          (t) => t.id === tutor.id
        );

        if (!existingTutors) {
          this.tutors$.next([...this.tutors$.value, tutor]);
        } else {
          this.tutors$.next(
            this.tutors$.value.map((t) => (t.id === tutor.id ? tutor : t))
          );
        }

        this.loading$.next(false);
      })
    );
  }

  findTutor(tutorId: string): Tutor | undefined {
    return this.tutors$.value.find((tutor) => tutor.id === tutorId);
  }

  createTutor(tutor: Tutor) {
    this.loading$.next(true);
    return this.http.post(`${this.baseUrl}/tutor`, { ...tutor }).pipe(
      tap((data: any) => {
        this.tutors$.next([...this.tutors$.value, data.data]);
        this.loading$.next(false);
      })
    );
  }

  deleteTutor(tutor_id: string) {
    this.loading$.next(true);
    return this.http.delete(`${this.baseUrl}/tutor/${tutor_id}`).pipe(
      tap((data: any) => {
        this.tutors$.next(this.tutors$.value.filter((t) => t.id !== tutor_id));
        this.loading$.next(false);
      })
    );
  }

  /** Deep-copies a tutor in the backend (settings, KB links, courses,
   *  FAQs, …). Internal name gets a "_copy" suffix. */
  duplicateTutor(tutor_id: string) {
    this.loading$.next(true);
    return this.http.post(`${this.baseUrl}/tutor/${tutor_id}/duplicate`, {}).pipe(
      tap({
        next: () => this.loading$.next(false),
        error: () => this.loading$.next(false),
      })
    );
  }

  updateTutor(tutor: Partial<Tutor>) {
    this.loading$.next(true);
    return this.http
      .patch(`${this.baseUrl}/tutor/${tutor.id}`, { ...tutor })
      .pipe(
        tap((data: any) => {
          // Merge instead of replace. The PATCH endpoint returns a
          // minimal projection (id/name/data_source_ids/…) — wholesale
          // replacement would drop fields like welcome_message,
          // created_at, etc. and force callers to re-fetch. Merging
          // keeps the local cache consistent and is robust against the
          // backend adding/removing fields without coordination.
          // `knowledge_mode_switched` is a one-off signal of this response
          // (see knowledge-select-form), not a field of the agent.
          const { knowledge_mode_switched, ...updated } = data.data ?? {};
          this.tutors$.next(
            this.tutors$.value.map((t) =>
              t.id === tutor.id ? { ...t, ...updated } : t
            )
          );
          this.loading$.next(false);
        })
      );
  }

  /** Central provider→model catalog + credit-tier hints for the granular
   *  model picker. Backed by al-tutor `GET /model-catalog`. */
  fetchModelCatalog() {
    return this.http
      .get<{ data: ModelCatalog }>(`${this.baseUrl}/model-catalog`)
      .pipe(map((r) => r.data));
  }

  fetchTutorSettings(tutor_id: string) {
    return this.http.get(`${this.baseUrl}/tutor/${tutor_id}/settings`).pipe(
      // Ensure the HTTP-loaded i18n bundle is available before settings
      // are pushed into the shared cache. Otherwise fresh agents can
      // render raw `templateDefaults.*` values in reused field components.
      switchMap((data: any) =>
        this.translate.get('templateDefaults').pipe(map(() => data))
      ),
      map((data: any) =>
        this.normalizeTemplateDefaultSettings(
          data.data.filter(
            (item: TutorSettingItem) => item.name !== 'Data Sources'
          )
        )
      )
    );
  }

  getTutorSettings(tutor_id: string) {
    this.loading$.next(true);
    return this.fetchTutorSettings(tutor_id).pipe(
      tap((settings: TutorSettingItem[]) => {
        this.tutorSettingsOwner = tutor_id;
        this.tutorSettings$.next(settings);
        this.loading$.next(false);
      })
    );
  }

  /** Empty `tutorSettings$` (another agent's settings must never be rendered
   *  for this one — see the agent shell in tutor-menu). */
  clearTutorSettings() {
    this.tutorSettingsOwner = null;
    this.tutorSettings$.next([]);
  }

  updateTutorSettings(tutor_id: string, settings: any) {
    this.loading$.next(true);
    return this.http
      .patch(`${this.baseUrl}/tutor/${tutor_id}/settings`, {
        tutor_id,
        settings,
      })
      .pipe(
        tap(() => {
          // Patch the local cache so subscribers (e.g. ai-settings.component
          // recomputing renderedTiles based on visibility_rule) see the new
          // value immediately. Without this, toggling an *_enabled flag in
          // the UI wouldn't show/hide its paired number input until the
          // page is reloaded.
          const current = this.tutorSettings$.value;
          const next = current.map((s) => {
            const v = settings[String(s.setting_id)];
            return v !== undefined ? { ...s, value: v } : s;
          });
          this.tutorSettings$.next(next);
          this.loading$.next(false);
        })
      );
  }

  /**
   * Fetch template-level settings (without bot instance values) — needed
   * by the flexible-template wizard to render default values before the
   * tutor is persisted.
   */
  getTemplateSettings(template_id: string) {
    return this.http
      .get(`${this.baseUrl}/template/${template_id}/settings`)
      .pipe(
        switchMap((data: any) =>
          this.translate.get('templateDefaults').pipe(map(() => data))
        ),
        map((data: any) =>
          this.normalizeTemplateDefaultSettings(
            data.data as TutorSettingItem[]
          )
        )
      );
  }

  /**
   * alpha_flexible: read the Tutor x Course x KB matrix for one tutor.
   */
  getCourseKBMatrix(tutor_id: string) {
    return this.http
      .get(`${this.baseUrl}/tutor/${tutor_id}/course-kb-matrix`)
      .pipe(map((data: any) => data.data as CourseKBRow[]));
  }

  /**
   * alpha_flexible: replace the full Tutor x Course x KB matrix atomically.
   */
  replaceCourseKBMatrix(tutor_id: string, rows: CourseKBRow[]) {
    return this.http
      .put(`${this.baseUrl}/tutor/${tutor_id}/course-kb-matrix`, { rows })
      .pipe(map((data: any) => data.data as CourseKBRow[]));
  }

  // -------------------------------------------------------------------
  // Per-bot FAQs (alpha_support / alpha_assist)
  // -------------------------------------------------------------------

  listBotFaqs(tutor_id: string) {
    return this.http
      .get(`${this.baseUrl}/tutor/${tutor_id}/faqs`)
      .pipe(map((data: any) => data.data as BotFaq[]));
  }

  createBotFaq(tutor_id: string, payload: BotFaqInput) {
    return this.http
      .post(`${this.baseUrl}/tutor/${tutor_id}/faqs`, payload)
      .pipe(map((data: any) => data as BotFaq));
  }

  updateBotFaq(tutor_id: string, faq_id: string, payload: BotFaqInput) {
    return this.http
      .put(`${this.baseUrl}/tutor/${tutor_id}/faqs/${faq_id}`, payload)
      .pipe(map((data: any) => data as BotFaq));
  }

  deleteBotFaq(tutor_id: string, faq_id: string) {
    return this.http.delete(
      `${this.baseUrl}/tutor/${tutor_id}/faqs/${faq_id}`
    );
  }

  // -------------------------------------------------------------------
  // Tutor ↔ Dozent link
  // -------------------------------------------------------------------

  /** The dozent-bot currently attached to this tutor, or null. */
  getAttachedDozent(tutor_id: string) {
    return this.http
      .get<AttachedDozent | null>(`${this.baseUrl}/tutor/${tutor_id}/dozent`)
      .pipe(map((d) => d ?? null));
  }

  attachDozent(tutor_id: string, dozent_bot_id: string) {
    return this.http.put<AttachedDozent>(
      `${this.baseUrl}/tutor/${tutor_id}/dozent`,
      { dozent_bot_id }
    );
  }

  detachDozent(tutor_id: string) {
    return this.http.delete(`${this.baseUrl}/tutor/${tutor_id}/dozent`);
  }

  /** All dozent-bots visible to the current user (admins see all in client). */
  listDozentBots() {
    return this.http
      .get<{ data: DozentBot[] }>(`${this.baseUrl}/dozent-bots`)
      .pipe(map((d) => d.data));
  }

  /** Candidate lecturers for a Twin's Moodle functions, plus who is picked.
   *  `candidates` comes from synced Moodle course roles; `pii_enabled` says
   *  whether names are available at all, and `site_admins_note` reminds the
   *  UI that Moodle site admins can never appear in synced data. */
  getMoodleDozentAccess(dozentBotId: string) {
    return this.http
      .get<{ data: MoodleDozentAccess }>(
        `${this.baseUrl}/dozent-bots/${dozentBotId}/moodle-access`
      )
      .pipe(map((d) => d.data));
  }

  /** Replace the curated list. Only consulted while the Twin's audience is
   *  `selected`; an empty list then admits nobody. */
  setMoodleDozentAccess(dozentBotId: string, moodleUserUuids: string[]) {
    return this.http
      .put<{ data: { selected: string[] } }>(
        `${this.baseUrl}/dozent-bots/${dozentBotId}/moodle-access`,
        { moodle_user_uuids: moodleUserUuids }
      )
      .pipe(map((d) => d.data));
  }

  /** Tutors that share a given dozent-bot — for the "linked tutors" list
   *  in the dozent admin page. */
  listLinkedTutors(dozent_bot_id: string) {
    return this.http
      .get<{ data: LinkedTutorSummary[] }>(
        `${this.baseUrl}/dozent-bots/${dozent_bot_id}/linked-tutors`
      )
      .pipe(map((d) => d.data));
  }

  /** Convenience for the wizard's "Neuen Dozent anlegen" button. */
  createDozentBot(input: { name: string; description?: string }) {
    return this.http.post<DozentBot>(`${this.baseUrl}/dozent-bots`, input);
  }

  // -------------------------------------------------------------------
  // Twin ↔ Tutor link requests (rights-limited dozent → admin approval)
  // -------------------------------------------------------------------

  /** Candidate tutors/flexible bots this twin could be linked to. */
  listLinkableTutors(dozent_bot_id: string) {
    return this.http
      .get<{ data: LinkableTutor[] }>(
        `${this.baseUrl}/dozent-bots/${dozent_bot_id}/linkable-tutors`
      )
      .pipe(map((d) => d.data));
  }

  /** Submit a request to link this twin to a target tutor. */
  createLinkRequest(
    dozent_bot_id: string,
    input: { target_tutor_bot_id: string; requester_note?: string | null }
  ) {
    return this.http.post<BotLinkRequest>(
      `${this.baseUrl}/dozent-bots/${dozent_bot_id}/link-requests`,
      input
    );
  }

  /** Admins/managers: all client requests; other dozents: own only. */
  listLinkRequests(status?: LinkRequestStatus) {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    return this.http
      .get<{ data: BotLinkRequest[] }>(`${this.baseUrl}/bot-link-requests`, {
        params,
      })
      .pipe(map((d) => d.data));
  }

  getLinkRequest(request_id: string) {
    return this.http.get<BotLinkRequest>(
      `${this.baseUrl}/bot-link-requests/${request_id}`
    );
  }

  approveLinkRequest(request_id: string, decision_note?: string | null) {
    return this.http.post<BotLinkRequest>(
      `${this.baseUrl}/bot-link-requests/${request_id}/approve`,
      { decision_note: decision_note ?? null }
    );
  }

  rejectLinkRequest(request_id: string, decision_note?: string | null) {
    return this.http.post<BotLinkRequest>(
      `${this.baseUrl}/bot-link-requests/${request_id}/reject`,
      { decision_note: decision_note ?? null }
    );
  }

  private resetState() {
    this.tutors$.next([]);
    this.tutorTemplates$.next([]);
    this.tutorSettings$.next([]);
    this.connections$.next([]);
    this.loading$.next(false);
  }
}
